import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { Badge, Group, Stack, Text, UnstyledButton } from '@mantine/core'
import { useReducedMotion } from '@mantine/hooks'
import { IconSparkles } from '@tabler/icons-react'
import type { CSSProperties } from 'react'

export const PERSONALIZED_FOR_YOUR = 'Personalized for your'
const AUTO_COLLAPSE_MS = 2000

const srOnly: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0,
}

type AutoCollapseReason = 'scroll' | 'timer' | null

export interface PersonalizedBadgeProps {
  /** Resolved chip labels, template order */
  answerChipLabels: Array<string>
  onEditCustomization: () => void
  /** `entry?.isIntersecting ?? true` for the quick stats row; false = scrolled past */
  isEstimatesInView: boolean
  /** Fingerprint so intersection/timer reset when answers change */
  labelFingerprint: string
}

export function PersonalizedBadge({
  answerChipLabels,
  onEditCustomization,
  isEstimatesInView,
  labelFingerprint,
}: PersonalizedBadgeProps) {
  const prefersReduced = useReducedMotion(false, { getInitialValueInEffect: false })
  const [autoCollapseReason, setAutoCollapseReason] = useState<AutoCollapseReason>(null)
  const prevInView = useRef<boolean | undefined>(undefined)
  const chipCount = answerChipLabels.length
  const showChips = chipCount <= 3 || autoCollapseReason === null
  const collapseControlId = useId()
  const chipRegionId = useId()
  const summaryText =
    chipCount > 0 ? `${chipCount} customizations` : 'Your customizations'

  const setExpandedChips = useCallback(() => {
    setAutoCollapseReason(null)
  }, [])

  // Reset when Q&A set changes
  useEffect(() => {
    setAutoCollapseReason(null)
    prevInView.current = undefined
  }, [labelFingerprint])

  // 2s idle collapse; reduced motion: skip timer (AC allows skip or immediate idle; we skip for usability)
  useEffect(() => {
    if (chipCount <= 3) return
    if (prefersReduced) return
    if (!showChips) return
    const t = setTimeout(() => {
      setAutoCollapseReason((r) => (r === null ? 'timer' : r))
    }, AUTO_COLLAPSE_MS)
    return () => clearTimeout(t)
  }, [chipCount, showChips, prefersReduced, labelFingerprint])

  // Scroll past / restore: only "scroll" reason is undone when estimates row re-enters
  useEffect(() => {
    if (chipCount <= 3) {
      prevInView.current = isEstimatesInView
      return
    }
    if (prevInView.current === undefined) {
      prevInView.current = isEstimatesInView
      return
    }
    const prev = prevInView.current
    prevInView.current = isEstimatesInView
    if (prev && !isEstimatesInView) {
      setAutoCollapseReason((r) => (r === null ? 'scroll' : r))
    }
    if (!prev && isEstimatesInView) {
      setAutoCollapseReason((r) => (r === 'scroll' ? null : r))
    }
  }, [isEstimatesInView, chipCount, labelFingerprint])

  if (chipCount === 0) return null

  return (
    <Stack gap="xs" data-testid="personalized-badge">
      <Group gap="xs" wrap="wrap" align="center">
        <Badge
          size="md"
          variant="light"
          color="yellow"
          leftSection={<IconSparkles size={14} aria-hidden style={{ display: 'inline' }} />}
          styles={{ root: { textTransform: 'none' } }}
        >
          {PERSONALIZED_FOR_YOUR}
        </Badge>
        <UnstyledButton
          type="button"
          onClick={onEditCustomization}
          aria-label="Edit plan customization"
        >
          <Text size="sm" c="dimmed" span>
            [Edit]
          </Text>
        </UnstyledButton>
      </Group>

      {chipCount <= 3 ? (
        <div
          role="region"
          data-testid="personalized-chips"
          aria-label="Your answer chips"
        >
          <Group gap="xs" wrap="wrap" aria-live="polite">
            {answerChipLabels.map((label, i) => (
              <Badge
                key={`${i}-${label}`}
                size="sm"
                color="yellow"
                variant="light"
                data-testid="personalized-chip"
              >
                {label}
              </Badge>
            ))}
          </Group>
        </div>
      ) : (
        <>
          <UnstyledButton
            type="button"
            onClick={!showChips ? setExpandedChips : undefined}
            id={collapseControlId}
            aria-expanded={showChips}
            aria-controls={chipRegionId}
            style={showChips ? srOnly : undefined}
            tabIndex={showChips ? -1 : 0}
            aria-label={
              showChips
                ? `${summaryText} — answer list expanded`
                : `${summaryText}. Show all answer chips.`
            }
          >
            {showChips ? (
              summaryText
            ) : (
              <Text size="sm" c="text" td="underline" fw={500}>
                {summaryText}
              </Text>
            )}
          </UnstyledButton>
          {showChips ? (
            <div
              id={chipRegionId}
              role="region"
              data-testid="personalized-chips"
              aria-label="Your answer chips"
            >
              <Group gap="xs" wrap="wrap" aria-live="polite">
                {answerChipLabels.map((label, i) => (
                  <Badge
                    key={`${i}-${label}`}
                    size="sm"
                    color="yellow"
                    variant="light"
                    data-testid="personalized-chip"
                  >
                    {label}
                  </Badge>
                ))}
              </Group>
            </div>
          ) : null}
        </>
      )}
    </Stack>
  )
}
